<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Button } from "@/components/ui/button";
  import { Spinner } from "@/components/ui/spinner";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { useMAssetLibraryStore, type GridDensity } from "@stores/mAssetLibrary";
  import ImageLibraryCard from "@/components/MCampaign/ImageLibrary/ImageLibraryCard.vue";

  const COLUMN_WIDTHS: Record<GridDensity, string> = { compact: "130px", comfortable: "180px", large: "260px" };

  const assetLibraryStore = useMAssetLibraryStore();
  const { searchQuery, activeTagFilters, assets, hasMore, isLoadingMore, view, density } =
    storeToRefs(assetLibraryStore);
  const { clearTagFilters, fetchMore, setSearchQuery } = assetLibraryStore;

  const gridStyle = computed(() => ({
    gridTemplateColumns: `repeat(auto-fill, minmax(${COLUMN_WIDTHS[density.value]}, 1fr))`,
  }));

  const emptyTitle = computed(() => {
    if (view.value === "out") return "No asset has been removed from the library.";
    if (view.value === "untagged") return "Every asset carries at least one tag.";
    if (view.value === "duplicates") return "No file is registered twice.";
    return "No assets match your filters.";
  });

  // The next page loads as soon as the end of the grid comes within reach of the scroll container.
  const sentinel = ref<HTMLElement | null>(null);
  let observer: IntersectionObserver | null = null;

  // The margin only extends the root: inside the picker modal the root has to be the modal's scrolling body.
  const scrollContainerOf = (element: HTMLElement) => {
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) return parent;
    }
    return null;
  };

  watch(sentinel, (element) => {
    observer?.disconnect();
    if (!element) return;
    observer = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && fetchMore(), {
      root: scrollContainerOf(element),
      rootMargin: "400px",
    });
    observer.observe(element);
  });

  // An observer only reports changes: a page too short to push the sentinel out of reach would stop the loading.
  watch(
    isLoadingMore,
    (loading) => {
      if (loading || !observer || !sentinel.value) return;
      observer.unobserve(sentinel.value);
      observer.observe(sentinel.value);
    },
    { flush: "post" },
  );

  onBeforeUnmount(() => observer?.disconnect());

  const resetFilters = () => {
    setSearchQuery("");
    clearTagFilters();
  };
</script>

<template>
  <div>
    <EmptyState
      v-if="!assets.length"
      class="py-16"
      :title="emptyTitle">
      <template
        v-if="activeTagFilters.length || searchQuery"
        #action>
        <Button
          variant="outline"
          @click="resetFilters">
          Clear filters
        </Button>
      </template>
    </EmptyState>

    <div
      v-else
      class="grid gap-4"
      :style="gridStyle">
      <ImageLibraryCard
        v-for="image in assets"
        :key="image.id"
        :image="image" />
    </div>

    <div
      v-if="hasMore && assets.length"
      ref="sentinel"
      class="flex justify-center py-6">
      <Spinner v-if="isLoadingMore" />
    </div>
  </div>
</template>
